import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { useLang } from '@/hooks/useLang';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = useLang();
  const supportedLangs = ['en', 'fr', 'de', 'da'];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function switchLang(lang: string) {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    if (supportedLangs.includes(parts[0])) {
      parts[0] = lang;
    } else {
      parts.unshift(lang);
    }
    const newPath = '/' + parts.join('/');
    i18n.changeLanguage(lang);
    navigate(newPath || `/${lang}/`);
    setLangOpen(false);
    setMobileOpen(false);
  }

  function getHomePath() {
    return `/${currentLang}/`;
  }

  function getPath(sub: string) {
    return `/${currentLang}/${sub}/`;
  }

  return (
    <nav className="sticky top-0 z-50 bg-white/98 backdrop-blur-md border-b border-background-200/80 shadow-sm">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={getHomePath()} className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
              AllChinaBuy
            </span>
            <span className="text-xs md:text-sm font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
              Spreadsheet
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home */}
            <a href={getHomePath()} className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors flex items-center gap-1.5">
              <i className="ri-home-line text-sm" />
              {t('nav.home')}
            </a>

            {/* Products */}
            <a href={getPath('products')} className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors flex items-center gap-1.5">
              <i className="ri-shopping-bag-3-line text-sm" />
              Products
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors"
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-background-200/80 rounded-xl shadow-lg shadow-black/5 overflow-hidden max-h-[70vh] overflow-y-auto">
                  <div className="p-2">
                    <p className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Product Categories</p>
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setCatOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                      >
                        <i className="ri-arrow-right-up-line text-primary-400 text-xs" />
                        {t(`categories.${cat.name.toLowerCase().replace(/[\s/]/g, '')}`) || cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Shopping Guide */}
            <a href={getPath('allchinabuy-spreadsheet')} className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors flex items-center gap-1.5">
              <i className="ri-book-open-line text-sm" />
              {t('nav.guide') || 'Guides'}
            </a>

            {/* Links to internal pages */}
            <a href={getPath('allchinabuy-review')} className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors">
              {t('nav.review')}
            </a>
            <a href={getPath('allchinabuy-shipping')} className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors">
              {t('nav.shipping')}
            </a>
            <a href={getPath('allchinabuy-coupon')} className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md transition-colors">
              {t('nav.coupon')}
            </a>

            {/* Main site CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap shadow-sm"
            >
              {t('mainSite.button')}
            </a>

            {/* Language Switcher */}
            <div className="relative ml-3" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-700 rounded-md border border-background-200/80 transition-colors"
              >
                <span className="uppercase text-xs font-bold">{currentLang}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-background-200/80 rounded-xl shadow-lg shadow-black/5 overflow-hidden">
                  {supportedLangs.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => switchLang(lang)}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        currentLang === lang
                          ? 'bg-primary-50 text-primary-700 font-semibold'
                          : 'text-foreground-800 hover:bg-background-100'
                      }`}
                    >
                      {t(`languages.${lang}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-foreground-900 hover:text-primary-700"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/80 bg-white px-4 py-5 space-y-1 max-h-[80vh] overflow-y-auto">
          <a href={getHomePath()} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 rounded-lg">
            <i className="ri-home-line text-primary-500" />
            {t('nav.home')}
          </a>
          <a href={getPath('products')} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 rounded-lg">
            <i className="ri-shopping-bag-3-line text-primary-500" />
            Products
          </a>
          <a href={getPath('allchinabuy-spreadsheet')} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 rounded-lg">
            <i className="ri-book-open-line text-primary-500" />
            {t('nav.guide') || 'Guides'}
          </a>
          <a href={getPath('allchinabuy-review')} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 rounded-lg">
            <i className="ri-star-line text-primary-500" />
            {t('nav.review')}
          </a>
          <a href={getPath('allchinabuy-shipping')} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 rounded-lg">
            <i className="ri-truck-line text-primary-500" />
            {t('nav.shipping')}
          </a>
          <a href={getPath('allchinabuy-coupon')} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 rounded-lg">
            <i className="ri-price-tag-line text-primary-500" />
            {t('nav.coupon')}
          </a>

          <div className="pt-3 mt-2 border-t border-background-200/80">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-3">{t('nav.categories')}</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                >
                  <i className="ri-arrow-right-up-line text-primary-400 text-xs" />
                  {t(`categories.${cat.name.toLowerCase().replace(/[\s/]/g, '')}`) || cat.name}
                </a>
              ))}
            </div>
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block mx-3 mt-3 px-3 py-3 text-sm font-semibold bg-primary-500 text-white rounded-lg text-center shadow-sm"
          >
            {t('mainSite.button')}
          </a>

          <div className="pt-3 mt-2 border-t border-background-200/80">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-3">{t('nav.language')}</p>
            <div className="flex gap-2 px-3">
              {supportedLangs.map((lang) => (
                <button
                  key={lang}
                  onClick={() => switchLang(lang)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors font-medium ${
                    currentLang === lang
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'border-background-200/80 text-foreground-800 hover:bg-background-100'
                  }`}
                >
                  {t(`languages.${lang}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}